import type {
  ApiItemType,
  ApiReturnProject,
  ApiReturnSite,
} from '@highimpact-frontend/api-types';
import {
  createContext,
  useContext,
  type ReactNode,
} from 'react';

import type { RemoteWidgetHostApi } from './remoteWidgetHostApi';
import type { RemoteWidgetHttpTransport } from './remoteWidgetHttpTransport';

export type RemoteWidgetNotificationSeverity = 'success' | 'error' | 'info';

export type RemoteWidgetHostMode = 'site' | 'standalone' | 'public';

export type RemoteWidgetHostGridType = 'grids' | 'reports';

export interface RemoteWidgetHostScope {
  readonly project_id: number | null;
  readonly site_id: number | null;
  readonly grid_id: number | null;
}

export interface RemoteWidgetHostContextValue {
  readonly locale: string;
  readonly getAuthToken: () => string | null;
  readonly scope: RemoteWidgetHostScope;
  readonly mode: RemoteWidgetHostMode;
  readonly grid_type: RemoteWidgetHostGridType;
  readonly site?: ApiReturnSite | null;
  readonly project?: ApiReturnProject | null;
  readonly checkPermission: (
    item: ApiItemType,
    extra?: Record<string, unknown>
  ) => boolean;
  readonly api: RemoteWidgetHostApi;
  readonly http: RemoteWidgetHttpTransport;
  readonly showNotification: (
    message: string,
    severity: RemoteWidgetNotificationSeverity
  ) => void;
  readonly buildDashboardUrl: (gridId: number) => string;
  readonly buildReportUrl: (gridId: number) => string;
}

const RemoteWidgetHostContext =
  createContext<RemoteWidgetHostContextValue | null>(null);

export interface RemoteWidgetHostProviderProps {
  readonly value: RemoteWidgetHostContextValue;
  readonly children: ReactNode;
}

export function RemoteWidgetHostProvider({
  value,
  children,
}: RemoteWidgetHostProviderProps) {
  return (
    <RemoteWidgetHostContext.Provider value={value}>
      {children}
    </RemoteWidgetHostContext.Provider>
  );
}

export function useRemoteWidgetHost(): RemoteWidgetHostContextValue {
  const context = useContext(RemoteWidgetHostContext);
  if (!context) {
    throw new Error(
      'useRemoteWidgetHost must be used within RemoteWidgetHostProvider'
    );
  }
  return context;
}

export function useOptionalRemoteWidgetHost():
  | RemoteWidgetHostContextValue
  | undefined {
  return useContext(RemoteWidgetHostContext) ?? undefined;
}
